import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useState } from "react";
import DropZone from "react-dropzone";
import { ImageComponent } from "@fidget/common/components/image/image";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { staticConfig } from "@fidget/common/config/static-config";
import { useStore } from "../../../../models";
import { CloserLoaderComponent } from "@fidget/common/components/loader/closerLoader";
import styles from "../../widgetConfig/widgetConfigSettings.css";
import { ImageUpload } from "../types";

interface Props extends DropzoneStateProps {
  image?: ImageUpload;
  onDelete?: () => void;
}

export const AgentAvatarSettingsDropZone = observer((props: Props) => {
  const { state, handleLoad } = useAgentAvatarSettingsDropZoneState(props);

  const renderLoader = () => (
    <div className={styles.loader}>
      <CloserLoaderComponent />
    </div>
  );

  const isLoading = state.type === DropzoneStateType.LOADING;
  const isError = state.type === DropzoneStateType.ERROR;

  const renderImagePreview = () => {
    return isLoading ? renderLoader() : <ImageComponent src={props.image?.url} />;
  };

  const renderDeleteImage = () => (
    <div className={styles.title} onClick={props.onDelete}>
      <L10n id={"settings.profile.picture.remove"} />
    </div>
  );

  const renderDropzone = () => (
    <DropZone
      className={classNames(styles["picture-section"], styles["logo-section"], {
        [styles.loading]: isLoading
      })}
      activeClassName={styles.active}
      accept={"image/jpeg, image/png"}
      onDrop={handleLoad}
    >
      <div className={classNames(styles.picture, styles.logo)}>{renderImagePreview()}</div>
      <div className={classNames(styles["picture-info"])}>
        <div className={styles.title}>
          <L10n id={"settings.widgetConfig.companyLogo.upload"} />
        </div>
        <div className={classNames(styles.description, { [styles.error]: isError })}>
          <L10n id={"settings.widgetConfig.companyLogo.upload.description"} />
        </div>
      </div>
      <div className={styles["dragdrop-info"]}>
        <div className={styles["bordered-container"]}>
          <div className={styles.text}>
            <i className={classNames("icon-upload", styles["upload-icon"])} />
            <L10n id={"settings.profile.picture.drag"} />
          </div>
        </div>
      </div>
    </DropZone>
  );

  return (
    <>
      {renderDropzone()}
      {renderDeleteImage()}
    </>
  );
});

interface DropzoneStateProps {
  onLoad?: (image: ImageUpload) => void;
}

enum DropzoneStateType {
  LOADING,
  LOADED,
  ERROR
}
enum DropzoneErrorType {
  FILE_TOO_BIG
}

interface DropzoneStateLoading {
  type: DropzoneStateType.LOADING;
}
interface DropzoneStateLoaded {
  type: DropzoneStateType.LOADED;
}
interface DropzoneStateError {
  type: DropzoneStateType.ERROR;
  error: DropzoneErrorType;
}

type DropzoneState = DropzoneStateLoaded | DropzoneStateLoading | DropzoneStateError;

export const useAgentAvatarSettingsDropZoneState = (props: DropzoneStateProps) => {
  const [state, setState] = useState<DropzoneState>({ type: DropzoneStateType.LOADED });
  const { uiStore } = useStore();

  const loadPicture = (picture: File) => {
    setState({ type: DropzoneStateType.LOADING });

    if (picture.size > staticConfig.maxPictureFileSizeBytes) {
      setState({ type: DropzoneStateType.ERROR, error: DropzoneErrorType.FILE_TOO_BIG });
      const message = intl.formatMessage(`settings.widgetConfig.upload.tooBig`);
      uiStore.addErrorToast(message);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      const result = event.target?.result;
      const base64 = isArrayBuffer(result)
        ? convertArrayBufferToString(result)
        : result || undefined;
      props.onLoad?.({ file: picture, url: base64 });
      setState({ type: DropzoneStateType.LOADED });
    };
    reader.readAsDataURL(picture);
  };

  const handleLoad = (pictures: File[], rejectedFiles: File[]) => {
    const picture = pictures[0];
    if (picture) {
      loadPicture(picture);
    }
    if (rejectedFiles.length) {
      const message = intl.formatMessage("settings.profile.picture.error.wrongFormat");
      uiStore.addErrorToast(message);
    }
  };

  return { state, handleLoad };
};

const isArrayBuffer = (arrayBuffer: unknown): arrayBuffer is ArrayBuffer =>
  arrayBuffer instanceof ArrayBuffer;
const convertArrayBufferToString = (arrayBuffer: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));
